import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';

import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import HomeScreen from './screens/HomeScreen';
import CatalogScreen from './screens/CatalogScreen';
import WaterDetailScreen from './screens/WaterDetailScreen';
import CartScreen from './screens/CartScreen';
import OrderScreen from './screens/OrderScreen';
import ProfileScreen from './screens/ProfileScreen';
import ChatScreen from './screens/ChatScreen';
import AdminOrdersScreen from './screens/AdminOrdersScreen';
import AdminOrderDetailsScreen from './screens/AdminOrderDetailsScreen';
import AdminMyWorkScreen from './screens/AdminMyWorkScreen';
import AdminCreateOrderScreen from './screens/AdminCreateOrderScreen';
import SuperAdminSettingsScreen from './screens/SuperAdminSettingsScreen';
import SuperAdminChatsScreen from './screens/SuperAdminChatsScreen';
import SuperAdminChatConversationScreen from './screens/SuperAdminChatConversationScreen';
import TabBar from './components/TabBar';
import WaterGlassLoader from './components/WaterGlassLoader';

function InstallPage() {
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);

  return (
    <div style={{ minHeight: '100vh', background: '#F0F8FF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
      <img src="/logo.png" alt="Акватория" style={{ width: 100, height: 100, borderRadius: 22, marginBottom: 20, objectFit: 'cover' }} />
      <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1A6DB5', marginBottom: 8 }}>Акватория</h1>
      <p style={{ fontSize: 16, color: '#6E6E73', marginBottom: 32 }}>Доставка питьевой воды</p>

      <div style={{ background: '#fff', borderRadius: 20, padding: 24, maxWidth: 360, width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <p style={{ fontSize: 17, fontWeight: 700, color: '#1C1C1E', marginBottom: 20 }}>
          Добавьте приложение на главный экран
        </p>

        {isIOS && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Step num={1} text={<>Нажмите кнопку <strong>«Поделиться»</strong> <span style={{ fontSize: 18 }}>⎙</span> внизу Safari</>} />
            <Step num={2} text={<>Выберите <strong>«На экран «Домой»»</strong> <span style={{ fontSize: 18 }}>➕</span></>} />
            <Step num={3} text={<>Нажмите <strong>«Добавить»</strong> — приложение появится на главном экране</>} />
          </div>
        )}

        {isAndroid && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Step num={1} text={<>Нажмите <strong>⋮</strong> (три точки) в правом верхнем углу Chrome</>} />
            <Step num={2} text={<>Выберите <strong>«Добавить на главный экран»</strong></>} />
            <Step num={3} text={<>Нажмите <strong>«Добавить»</strong></>} />
          </div>
        )}

        {!isIOS && !isAndroid && (
          <p style={{ fontSize: 15, color: '#6E6E73', lineHeight: 1.6 }}>
            Откройте эту страницу на смартфоне (iPhone или Android) и добавьте на главный экран.
          </p>
        )}
      </div>

      <p style={{ marginTop: 24, fontSize: 13, color: '#8E8E93' }}>
        После добавления запустите приложение с главного экрана
      </p>
    </div>
  );
}

function Step({ num, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, textAlign: 'left' }}>
      <div style={{ width: 32, height: 32, borderRadius: 16, background: '#1A6DB5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>
        {num}
      </div>
      <p style={{ fontSize: 15, color: '#1C1C1E', lineHeight: 1.5, paddingTop: 4 }}>{text}</p>
    </div>
  );
}

function AppContent() {
  const { user, profileLoading, isDark } = useAppContext();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  if (profileLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 20 }}>
        <WaterGlassLoader color="#1A6DB5" size={1.4} />
        <div style={{ color: 'var(--primary)', fontWeight: 600, fontSize: 15 }}>Акватория</div>
      </div>
    );
  }

  if (!user) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/register" element={<RegisterScreen />} />
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <div style={{ paddingBottom: 'calc(64px + env(safe-area-inset-bottom))' }}>
        <Routes>
          {/* Клиент */}
          {!user.isAdmin && <Route path="/" element={<HomeScreen />} />}
          <Route path="/catalog" element={<CatalogScreen />} />
          <Route path="/water-detail" element={<WaterDetailScreen />} />
          <Route path="/cart" element={<CartScreen />} />
          <Route path="/order" element={<OrderScreen />} />
          <Route path="/chat" element={<ChatScreen />} />

          {/* Общий */}
          <Route path="/profile" element={<ProfileScreen />} />

          {/* Админ */}
          {user.isAdmin && <Route path="/" element={<AdminOrdersScreen />} />}
          {user.isAdmin && <Route path="/create-order" element={<AdminCreateOrderScreen />} />}
          {user.isAdmin && <Route path="/admin-orders" element={<AdminOrdersScreen />} />}
          {user.isAdmin && <Route path="/admin-order/:orderId" element={<AdminOrderDetailsScreen />} />}
          {user.isAdmin && <Route path="/admin-my-work" element={<AdminMyWorkScreen />} />}

          {/* Суперадмин */}
          {user.isSuperAdmin && <Route path="/superadmin" element={<SuperAdminSettingsScreen />} />}
          {user.isSuperAdmin && <Route path="/superadmin-chats" element={<SuperAdminChatsScreen />} />}
          {user.isSuperAdmin && <Route path="/superadmin-chat/:userId" element={<SuperAdminChatConversationScreen />} />}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <TabBar />
    </BrowserRouter>
  );
}

export default function App() {
  const isStandalone = window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;

  if (!isStandalone) return <InstallPage />;

  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}
